import {useMutation, useQueryClient} from '@tanstack/react-query'
import {httpMarkNotificationAsRead} from '../http/notifications.http'
import {QUERY_KEYS} from '@/queryClient'
import {NotificationCount} from '../types'
import {updateNotificationsInCache} from '../utils/cacheUtils'

/**
 * Hook to mark a notification as read
 */
export const useMarkNotificationAsRead = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (notificationId: string) => {
            const {data} = await httpMarkNotificationAsRead({urlParams: {notificationId}})
            return data
        },
        onMutate: (notificationId: string) => {
            const now = new Date()

            // Optimistically update notification count
            queryClient.setQueryData<NotificationCount>([QUERY_KEYS.NOTIFICATION_COUNT], oldData => {
                if (!oldData) return {toRead: 0, total: 0}
                return {
                    ...oldData,
                    toRead: Math.max(0, oldData.toRead - 1)
                }
            })

            // Update the specific notification's readAt field
            updateNotificationsInCache(queryClient, notification =>
                notification.id === notificationId ? {...notification, readAt: now} : notification
            )

            return {previousCount: queryClient.getQueryData([QUERY_KEYS.NOTIFICATION_COUNT])}
        },
        onError: (_, __, context) => {
            // Restore previous count on error
            if (context?.previousCount) {
                queryClient.setQueryData([QUERY_KEYS.NOTIFICATION_COUNT], context.previousCount)
            }
        },
        onSettled: () => {
            // Refetch notifications to ensure data consistency
            queryClient.invalidateQueries({queryKey: [QUERY_KEYS.NOTIFICATIONS]})
        }
    })
}
